import React from 'react';
import { C } from '../constants';
import { SERVICE_CATALOG } from '../services/catalog';
import { hubPost, hubConfigurado, hubApiBaseUrl } from '../services/hub';

// ─────────────────────────────────────────────────────────────────────────────
// DX DE SERVICIOS (admin) — tablero de los servicios de la plataforma de la suite Alebas,
// al estilo de FluxAI. Dos secciones:
//   1) Catálogo de entitlements: cada servicio (Solar Hub + FluxAI), su tier (gratis/premium),
//      audiencia y si REQUIERE CONTRATO. La consola valida por usuario con `tieneServicio(sku)`
//      del JWT (servicios[] que emite la base única: api/ de FluxAI). Aquí el admin lo AUDITA.
//   2) Diagnóstico de integraciones: prueba el backend propio (`/hub-api`)
//      y muestra estado/latencia, sin efectos secundarios (endpoints de solo lectura).
// ─────────────────────────────────────────────────────────────────────────────

// Integraciones de SOLO LECTURA seguras para "probar" (no escriben nada).
const PROBES = [
  { key: 'trm', label: 'TRM (Banco República)', path: 'trm', body: {} },
  { key: 'xm', label: 'XM (precio de bolsa)', path: 'xm-spot', body: {} },
  { key: 'cec', label: 'CEC (paneles/inversores)', path: 'cec', body: { q: '', limit: 1 } },
];

// Chip sobrio (técnico): contorno muted, sin relleno de color; acento solo para destacar.
function Badge({ children, accent }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
      color: accent || C.muted, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 5, padding: '3px 7px',
    }}>{children}</span>
  );
}

export default function ServicesDx({ ss }) {
  const [results, setResults] = React.useState({}); // key → { state:'ok'|'fail'|'run', ms, msg }

  const probe = async (p) => {
    setResults(r => ({ ...r, [p.key]: { state: 'run' } }));
    const t0 = (typeof performance !== 'undefined' ? performance.now() : 0);
    try {
      await hubPost(p.path, p.body, { timeoutMs: 12000 });
      const ms = Math.round((typeof performance !== 'undefined' ? performance.now() : 0) - t0);
      setResults(r => ({ ...r, [p.key]: { state: 'ok', ms } }));
    } catch (e) {
      const ms = Math.round((typeof performance !== 'undefined' ? performance.now() : 0) - t0);
      setResults(r => ({ ...r, [p.key]: { state: 'fail', ms, msg: e?.message || 'Error' } }));
    }
  };
  const probeAll = () => PROBES.forEach(probe);

  const solarHub = SERVICE_CATALOG.filter(s => s.suite !== 'fluxai');
  const fluxai = SERVICE_CATALOG.filter(s => s.suite === 'fluxai');
  const mapsKey = !!(process.env.REACT_APP_GOOGLE_MAPS_KEY || (typeof window !== 'undefined' && window.google?.maps));

  const ServiceRow = (s) => (
    <div key={s.sku} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
      background: C.dark, border: `1px solid ${C.border}`, borderRadius: 8, flexWrap: 'wrap',
    }}>
      <div style={{ width: 30, height: 30, borderRadius: 7, background: `${C.muted}14`, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: C.muted, flexShrink: 0 }}>{(s.nombre || '?').charAt(0)}</div>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>{s.nombre}</div>
        <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.3 }}>{s.desc}</div>
      </div>
      <code style={{ fontSize: 9, color: C.muted, background: `${C.muted}14`, padding: '2px 6px', borderRadius: 4 }}>{s.sku}</code>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        <Badge>{s.suite === 'fluxai' ? 'FluxAI' : 'Solar Hub'}</Badge>
        <Badge accent={s.tier === 'premium' ? C.amber : undefined}>{s.tier === 'premium' ? 'Premium' : 'Gratis'}</Badge>
        {s.requiereContrato && <Badge>Requiere contrato</Badge>}
      </div>
    </div>
  );

  const dot = (state) => state === 'ok' ? '#00c781' : state === 'fail' ? C.red : state === 'run' ? C.amber : C.muted;

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 800, color: C.heading, marginBottom: 4 }}>DX de servicios</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 16, lineHeight: 1.5 }}>
        Servicios de la suite Alebas <strong style={{ color: C.heading }}>embebidos por plataforma</strong>. Cada uno se
        valida por usuario contra lo que tiene <strong style={{ color: C.heading }}>contratado/pagado</strong>
        (claim <code>servicios[]</code> del JWT — base única: <code>api/</code> de FluxAI). Aquí el admin audita el
        catálogo y el estado de las integraciones.
      </div>

      {/* Catálogo de entitlements */}
      <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, margin: '6px 0 8px' }}>Catálogo de Solar Hub</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>{solarHub.map(ServiceRow)}</div>

      <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, margin: '6px 0 8px' }}>Servicios FluxAI (telemetría / nivel máquina)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>{fluxai.map(ServiceRow)}</div>

      {/* Diagnóstico de integraciones */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', margin: '6px 0 8px' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.heading }}>Diagnóstico de integraciones</div>
        <button onClick={probeAll} style={{ ...ss.btn, padding: '7px 14px', fontSize: 12 }}>Probar todo</button>
      </div>

      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
        Backend (hub-api): <code>{hubConfigurado() ? (hubApiBaseUrl() || '/hub-api') : 'no configurado'}</code>
        {' · '}Google Maps key: <strong style={{ color: mapsKey ? '#00c781' : C.red }}>{mapsKey ? 'configurada' : 'no detectada'}</strong>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {PROBES.map(p => {
          const r = results[p.key] || {};
          return (
            <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: C.dark, border: `1px solid ${C.border}`, borderRadius: 8, flexWrap: 'wrap' }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: dot(r.state), flexShrink: 0 }} />
              <div style={{ flex: '1 1 180px', fontSize: 13, fontWeight: 600, color: C.heading }}>{p.label}</div>
              <div style={{ flex: '1 1 160px', fontSize: 11, color: r.state === 'fail' ? C.red : C.muted, minWidth: 0 }}>
                {r.state === 'run' ? 'Probando…'
                  : r.state === 'ok' ? `OK · ${r.ms} ms`
                  : r.state === 'fail' ? `Falla · ${r.msg}`
                  : 'Sin probar'}
              </div>
              <button onClick={() => probe(p)} disabled={r.state === 'run'} style={{ ...ss.ghost, padding: '6px 12px', fontSize: 11, opacity: r.state === 'run' ? 0.6 : 1 }}>Probar</button>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, fontSize: 11, color: C.muted, lineHeight: 1.5, background: `${C.teal}0d`, border: `1px solid ${C.teal}22`, borderRadius: 10, padding: '12px 14px' }}>
        Las pruebas usan endpoints de <strong style={{ color: C.heading }}>solo lectura</strong> (no escriben datos) a través del
        proxy same-origin de Vercel — por eso no dependen del CORS del backend. Los servicios de FluxAI se habilitan por
        contrato y se consumen con la misma sesión de la suite.
      </div>
    </div>
  );
}
